import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import {
  clearMerchantToken,
  merchantFetch,
  type MerchantRole,
} from "../lib/merchantAuth";
import { Wordmark } from "./Brand";

/**
 * Shared merchant-dashboard nav strip.
 *
 * History:
 *   • Phase 40 added role-gated filtering (OWNER / MANAGER / STAFF).
 *   • Phase 41 added a Help tab.
 *   • The follow-up to the admin-nav reorganization brought the same
 *     dropdown grouping here: 13 flat tabs were wrapping onto two
 *     rows on common laptop widths and made it hard to find the right
 *     page. Same-purpose tabs are now grouped into click-to-open
 *     categories (Operate / Programs / Settings) with high-traffic
 *     surfaces (Dashboard, Inbox, Help) kept flat.
 *
 * Categories (each is a dropdown, role-filtered):
 *   • Operate   → Transactions, Customers, Redeem        (cashier flow)
 *   • Programs  → Rewards, Surprises, Streaks, Marketplace (loyalty toolkit)
 *   • Plan      → Plans (OWNER), SMS credits              (billing)
 *   • Settings  → Settings, Team (OWNER)                  (config)
 *
 * Top-level flat:
 *   • Dashboard (OWNER/MANAGER)
 *   • Inbox     (OWNER/MANAGER)
 *   • Help      (all roles)
 *   • Sign out  (always visible, right-aligned)
 *
 * If a group has only a single item left after role-filtering it is
 * rendered as a flat link (using the item's own label) so STAFF and
 * MANAGER don't get pointless 1-item dropdowns. Dashboard, Inbox, and
 * Help are intentionally outside any group because they're the most
 * common destinations and shouldn't require an extra click.
 */
export type MerchantNavTab =
  | "dashboard"
  | "transactions"
  | "customers"
  | "redeem"
  | "rewards"
  | "marketplace"
  | "surprises"
  | "streaks"
  | "inbox"
  | "plans"
  | "team"
  | "settings"
  | "sms-credits"
  | "help";

interface NavItem {
  key: MerchantNavTab;
  label: string;
  href: string;
  // Roles allowed to see this item.
  roles: MerchantRole[];
}

interface NavEntry {
  // Flat single link OR a dropdown group of links.
  kind: "link" | "group";
  // Stable identifier (used as React key + dropdown open-state key).
  key: string;
  // Visible label on the trigger / link.
  label: string;
  // Single-link entries.
  item?: NavItem;
  // Group entries.
  items?: NavItem[];
}

const ENTRIES: NavEntry[] = [
  {
    kind: "link",
    key: "dashboard",
    label: "Dashboard",
    item: {
      key: "dashboard",
      label: "Dashboard",
      href: "/merchant/dashboard",
      roles: ["OWNER", "MANAGER"],
    },
  },
  {
    kind: "group",
    key: "operate",
    label: "Operate",
    items: [
      {
        key: "transactions",
        label: "Transactions",
        href: "/merchant/transactions",
        roles: ["OWNER", "MANAGER", "STAFF"],
      },
      {
        key: "customers",
        label: "Customers",
        href: "/merchant/customers",
        roles: ["OWNER", "MANAGER", "STAFF"],
      },
      {
        key: "redeem",
        label: "Redeem",
        href: "/merchant/redeem",
        roles: ["OWNER", "MANAGER", "STAFF"],
      },
    ],
  },
  {
    kind: "group",
    key: "programs",
    label: "Programs",
    items: [
      {
        key: "rewards",
        label: "Rewards",
        href: "/merchant/rewards",
        roles: ["OWNER", "MANAGER"],
      },
      {
        key: "surprises",
        label: "Surprises",
        href: "/merchant/surprises",
        roles: ["OWNER", "MANAGER"],
      },
      {
        key: "streaks",
        label: "Streaks",
        href: "/merchant/streaks",
        roles: ["OWNER", "MANAGER"],
      },
      {
        key: "marketplace",
        label: "Marketplace",
        href: "/merchant/marketplace",
        roles: ["OWNER", "MANAGER"],
      },
    ],
  },
  {
    kind: "link",
    key: "inbox",
    label: "Inbox",
    item: {
      key: "inbox",
      label: "Inbox",
      href: "/merchant/inbox",
      roles: ["OWNER", "MANAGER"],
    },
  },
  {
    kind: "group",
    key: "plan",
    label: "Plan",
    items: [
      {
        key: "plans",
        label: "Plans",
        href: "/merchant/plans",
        roles: ["OWNER"],
      },
      {
        key: "sms-credits",
        label: "SMS credits",
        href: "/merchant/sms-credits",
        roles: ["OWNER", "MANAGER"],
      },
    ],
  },
  {
    kind: "group",
    key: "settings",
    label: "Settings",
    items: [
      {
        key: "settings",
        label: "Settings",
        href: "/merchant/settings",
        roles: ["OWNER", "MANAGER"],
      },
      {
        key: "team",
        label: "Team",
        href: "/merchant/team",
        roles: ["OWNER"],
      },
    ],
  },
  {
    kind: "link",
    key: "help",
    label: "Help",
    item: {
      key: "help",
      label: "Help",
      href: "/merchant/help",
      roles: ["OWNER", "MANAGER", "STAFF"],
    },
  },
];

interface MeResponse {
  user: { role: MerchantRole };
}

interface DropdownProps {
  groupKey: string;
  groupLabel: string;
  items: NavItem[];
  current: MerchantNavTab;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

function Dropdown({
  groupKey,
  groupLabel,
  items,
  current,
  open,
  onToggle,
  onClose,
}: DropdownProps) {
  const containsActive = items.some((i) => i.key === current);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-haspopup="menu"
        className={
          "inline-flex items-center gap-1 text-sm " +
          (containsActive
            ? "font-semibold text-navy"
            : "text-ink-dark hover:text-navy")
        }
        data-testid={`merchant-nav-group-${groupKey}`}
      >
        {groupLabel}
        <svg
          className="h-3 w-3"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path d="M3 5l3 3 3-3" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full mt-1 z-40 min-w-[160px] rounded-md border border-amber-light bg-white shadow-lg py-1"
        >
          {items.map((it) => (
            <Link
              key={it.key}
              href={it.href}
              onClick={onClose}
              role="menuitem"
              className={
                "block px-3 py-2 text-sm " +
                (current === it.key
                  ? "bg-amber-light text-navy font-semibold"
                  : "text-navy hover:bg-amber-light")
              }
              data-testid={`merchant-nav-item-${it.key}`}
            >
              {it.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function MerchantNav({ current }: { current: MerchantNavTab }) {
  // Default to OWNER while loading so the legacy-bearer cashier sees
  // the full menu without a flicker (server still 403s anything STAFF
  // shouldn't reach, even if we got the role wrong).
  const [role, setRole] = useState<MerchantRole>("OWNER");
  const [openKey, setOpenKey] = useState<string | null>(null);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await merchantFetch<MeResponse>("/api/v1/tenant/auth/me");
      if (!cancelled && r.ok && r.data?.user?.role) {
        setRole(r.data.user.role);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Close the open dropdown on outside-click or Escape — mirrors the
  // admin nav and the standard menubar pattern so keyboard users can
  // dismiss without a mouse.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!navRef.current?.contains(e.target as Node)) setOpenKey(null);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpenKey(null);
    }
    document.addEventListener("click", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Build the role-filtered visible entries. A group with zero
  // accessible items is hidden; a group with exactly one is collapsed
  // into a flat link so we don't render pointless 1-item dropdowns
  // (the common case for MANAGER's Settings group).
  const visible: NavEntry[] = ENTRIES.flatMap((entry): NavEntry[] => {
    if (entry.kind === "link") {
      return entry.item!.roles.includes(role) ? [entry] : [];
    }
    const filtered = entry.items!.filter((i) => i.roles.includes(role));
    if (filtered.length === 0) return [];
    if (filtered.length === 1) {
      const only = filtered[0]!;
      return [
        {
          kind: "link",
          key: only.key,
          label: only.label,
          item: only,
        },
      ];
    }
    return [{ ...entry, items: filtered }];
  });

  return (
    <>
      <header className="flex items-center justify-between">
        <Wordmark size="sm" />
        <button
          type="button"
          onClick={() => {
            clearMerchantToken();
            window.location.href = "/merchant/login";
          }}
          className="text-sm text-ink-dark hover:text-navy"
        >
          Sign out
        </button>
      </header>
      <nav
        ref={navRef}
        className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm"
      >
        {visible.map((entry) => {
          if (entry.kind === "link") {
            const it = entry.item!;
            return current === it.key ? (
              <span
                key={entry.key}
                className="font-semibold text-navy"
                data-testid={`merchant-nav-current-${it.key}`}
              >
                {it.label}
              </span>
            ) : (
              <Link
                key={entry.key}
                href={it.href}
                className="text-ink-dark hover:text-navy"
                data-testid={`merchant-nav-link-${it.key}`}
              >
                {it.label}
              </Link>
            );
          }
          return (
            <Dropdown
              key={entry.key}
              groupKey={entry.key}
              groupLabel={entry.label}
              items={entry.items!}
              current={current}
              open={openKey === entry.key}
              onToggle={() =>
                setOpenKey(openKey === entry.key ? null : entry.key)
              }
              onClose={() => setOpenKey(null)}
            />
          );
        })}
      </nav>
    </>
  );
}
